import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth, api } from '../App';
import NotificationBell from './NotificationBell';
import { LayoutDashboard, Rocket, FileText, BarChart3, Shield, Settings, LogOut, Menu, X, Link2, Crown, Users, DollarSign, MessageCircle, HeadphonesIcon, GripVertical, UserCheck, Lightbulb, ChevronDown, ChevronRight, Megaphone, BookOpen, Contact, UsersRound } from 'lucide-react';

const PANZIX_LOGO_URL = 'https://customer-assets.emergentagent.com/job_cc5301f8-029c-4cb1-afaa-ab22368334bb/artifacts/yofpexsn_Logo%20-%20official%20300x300.png';

const SUBSCRIBER_NAV = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/my-team', icon: UserCheck, label: 'My AI Team' },
  { to: '/deploy', icon: Rocket, label: 'Deploy AI Employee' },
  { to: '/employees', icon: Contact, label: 'Employee Directory' },
  { to: '/templates', icon: BookOpen, label: 'Templates' },
  { to: '/documents', icon: FileText, label: 'Documents' },
  { to: '/reports', icon: BarChart3, label: 'Reports' },
  { to: '/integrations', icon: Link2, label: 'Integrations' },
  { to: '/feature-requests', icon: Lightbulb, label: 'Feature Requests' },
  { to: '/community', icon: UsersRound, label: 'Community' },
  { to: '/support', icon: HeadphonesIcon, label: 'Support' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

const OWNER_NAV = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true, section: null, group: null },
  { to: '/owner/subscribers', icon: Crown, label: 'Subscribers', section: 'OWNER SPACE', group: 'OWNER SPACE' },
  { to: '/owner/users', icon: Users, label: 'All Users', section: null, group: 'OWNER SPACE' },
  { to: '/owner/financials', icon: DollarSign, label: 'Financials', section: null, group: 'OWNER SPACE' },
  { to: '/support', icon: MessageCircle, label: 'Support Inbox', section: null, group: 'OWNER SPACE' },
  { to: '/feature-requests', icon: Lightbulb, label: 'Feature Requests', section: null, group: 'OWNER SPACE' },
  { to: '/community', icon: UsersRound, label: 'Community', section: null, group: 'OWNER SPACE' },
  { to: '/admin', icon: Shield, label: 'System Admin', section: 'PLATFORM', group: 'PLATFORM' },
  { to: '/deploy', icon: Rocket, label: 'Deploy AI Employee', section: 'SUBSCRIBER TOOLS', group: 'SUBSCRIBER TOOLS' },
  { to: '/my-team', icon: UserCheck, label: 'My AI Team', section: null, group: 'SUBSCRIBER TOOLS' },
  { to: '/employees', icon: Contact, label: 'Employee Directory', section: null, group: 'SUBSCRIBER TOOLS' },
  { to: '/templates', icon: BookOpen, label: 'Templates', section: null, group: 'SUBSCRIBER TOOLS' },
  { to: '/documents', icon: FileText, label: 'Documents', section: null, group: 'SUBSCRIBER TOOLS' },
  { to: '/reports', icon: BarChart3, label: 'Reports', section: null, group: 'SUBSCRIBER TOOLS' },
  { to: '/integrations', icon: Link2, label: 'Integrations', section: null, group: 'SUBSCRIBER TOOLS' },
  { to: '/settings', icon: Settings, label: 'Settings', section: null, group: 'SUBSCRIBER TOOLS' },
];

function CompanySetupModal({ onComplete }) {
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [size, setSize] = useState('');
  const [loading, setLoading] = useState(false);
  const { refreshUser } = useAuth();
  const { api } = require('../App');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/api/company/setup', { name, industry, size });
      await refreshUser();
      onComplete();
      // Trigger subscription check
      window.dispatchEvent(new Event('company-setup-complete'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-overlay">
      <div className="setup-card slide-up">
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <img src={PANZIX_LOGO_URL} alt="Panzix" style={{ width: 60, height: 60, borderRadius: 12 }} />
        </div>
        <h2>Welcome to Panzix HR AI Workforce!</h2>
        <p className="subtitle">Let's set up your company to get started.</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Company Name</label>
            <input data-testid="company-name" value={name} onChange={e => setName(e.target.value)} placeholder="Acme Corp" required />
          </div>
          <div className="form-group">
            <label>Industry</label>
            <select data-testid="company-industry" value={industry} onChange={e => setIndustry(e.target.value)} required
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none' }}>
              <option value="">Select industry...</option>
              <option value="technology">Technology</option>
              <option value="healthcare">Healthcare</option>
              <option value="finance">Finance</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="retail">Retail</option>
              <option value="education">Education</option>
              <option value="consulting">Consulting</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Company Size</label>
            <select data-testid="company-size" value={size} onChange={e => setSize(e.target.value)} required
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none' }}>
              <option value="">Select size...</option>
              <option value="1-10">1-10 employees</option>
              <option value="11-50">11-50 employees</option>
              <option value="51-200">51-200 employees</option>
              <option value="201-500">201-500 employees</option>
              <option value="500+">500+ employees</option>
            </select>
          </div>
          <button type="submit" className="btn-primary" disabled={loading || !name || !industry || !size} data-testid="setup-submit">
            {loading ? 'Setting up...' : 'Get Started'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isOwnerOrStaff = user?.role === 'owner' || user?.role === 'panzix_employee';
  const [showSetup, setShowSetup] = useState(!user?.company_id && !isOwnerOrStaff);
  const [mobileNav, setMobileNav] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState(null);

  const navItems = isOwnerOrStaff ? OWNER_NAV : SUBSCRIBER_NAV;

  // Fetch profile image and company logo
  const loadCustomization = useCallback(async () => {
    try {
      const profileRes = await api.get('/api/auth/profile/image');
      if (profileRes.data?.profile_image) setProfileImage(profileRes.data.profile_image);
    } catch (e) { console.error('Profile load error:', e); }
    try {
      const logoRes = await api.get('/api/company/logo');
      if (logoRes.data?.logo) setCompanyLogo(logoRes.data.logo);
    } catch (e) { console.error('Logo load error:', e); }
    try {
      const companyRes = await api.get('/api/company');
      if (companyRes.data?.name) setCompanyName(companyRes.data.name);
    } catch (e) { console.error('Company load error:', e); }
  }, []);

  useEffect(() => { loadCustomization(); }, [location.pathname, loadCustomization]);

  const getPageTitle = () => {
    if (location.pathname === '/') return 'Dashboard';
    if (location.pathname === '/deploy') return 'Deploy AI Employee';
    if (location.pathname === '/my-team') return 'My AI Team';
    if (location.pathname.startsWith('/workspace')) return 'Agent Workspace';
    if (location.pathname === '/documents') return 'Documents';
    if (location.pathname === '/reports') return 'Reports';
    if (location.pathname === '/integrations') return 'Integrations';
    if (location.pathname === '/admin') return 'Admin Panel';
    if (location.pathname === '/support') return isOwnerOrStaff ? 'Support Inbox' : 'Support';
    if (location.pathname === '/owner/subscribers') return 'Subscribers';
    if (location.pathname === '/owner/users') return 'All Users';
    if (location.pathname === '/owner/financials') return 'Financials';
    if (location.pathname === '/settings') return 'Settings';
    if (location.pathname === '/feature-requests') return 'Feature Requests';
    if (location.pathname === '/employees') return 'Employee Directory';
    if (location.pathname === '/announcements') return 'Announcements';
    if (location.pathname === '/templates') return 'Templates';
    if (location.pathname === '/community') return 'Community';
    return 'Panzix HR AI Workforce';
  };

  const getRoleBadge = () => {
    if (user?.role === 'owner') return <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>OWNER</span>;
    if (user?.role === 'panzix_employee') return <span style={{ fontSize: 10, background: '#f3e8ff', color: '#7c3aed', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>STAFF</span>;
    return null;
  };

  // Drag-and-drop reordering for sidebar items (non-section items only for subscribers)
  const STORAGE_KEY = `panzix_hr_nav_order_${user?.role || 'user'}`;
  const [orderedNav, setOrderedNav] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const savedOrder = JSON.parse(saved);
        // Re-map saved order to actual items
        const itemMap = {};
        navItems.forEach(item => { itemMap[item.to] = item; });
        const ordered = savedOrder.filter(to => itemMap[to]).map(to => itemMap[to]);
        // Add any new items not in saved order
        navItems.forEach(item => {
          if (!ordered.find(o => o.to === item.to)) ordered.push(item);
        });
        return ordered;
      }
    } catch (e) { console.error(e); }
    return navItems;
  });

  useEffect(() => {
    setOrderedNav(prev => {
      const itemMap = {};
      navItems.forEach(item => { itemMap[item.to] = item; });
      const ordered = prev.filter(p => itemMap[p.to]).map(p => itemMap[p.to]);
      navItems.forEach(item => {
        if (!ordered.find(o => o.to === item.to)) ordered.push(item);
      });
      return ordered;
    });
  }, [isOwnerOrStaff, navItems]);

  const [dragIdx, setDragIdx] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem('panzix_hr_collapsed') || '{}'); } catch(e) { return {}; }
  });

  const toggleSection = (section) => {
    const updated = { ...collapsedSections, [section]: !collapsedSections[section] };
    setCollapsedSections(updated);
    localStorage.setItem('panzix_hr_collapsed', JSON.stringify(updated));
  };

  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const newOrder = [...orderedNav]; const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(idx, 0, moved); setOrderedNav(newOrder); setDragIdx(idx);
  };
  const handleDragEnd = () => { setDragIdx(null); localStorage.setItem(STORAGE_KEY, JSON.stringify(orderedNav.map(i => i.to))); };

  // Group items by section for collapsible rendering
  const renderNavItems = () => {
    const elements = [];
    const renderedSections = new Set();
    orderedNav.forEach((item, idx) => {
      // Render section header if this item starts a new section
      if (item.section && !renderedSections.has(item.section)) {
        renderedSections.add(item.section);
        const isCollapsed = collapsedSections[item.section];
        elements.push(
          <div key={`section-${item.section}`} onClick={() => toggleSection(item.section)}
            style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: 1, padding: '10px 16px 4px', textTransform: 'uppercase', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}>
            <span>{item.section}</span>
            {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
          </div>
        );
      }
      // Check if this item's group is collapsed
      const isHidden = item.group && collapsedSections[item.group];
      if (!isHidden) {
        elements.push(
          <div key={item.to + item.label} draggable onDragStart={e => handleDragStart(e, idx)} onDragOver={e => handleDragOver(e, idx)} onDragEnd={handleDragEnd}
            style={{ opacity: dragIdx === idx ? 0.5 : 1, transition: 'all 0.15s' }}>
            <NavLink to={item.to} end={item.end}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileNav(false)} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
              <GripVertical size={12} style={{ opacity: 0.2, cursor: 'grab', flexShrink: 0 }} />
              <item.icon size={17} />
              <span>{item.label}</span>
            </NavLink>
          </div>
        );
      }
    });
    return elements;
  };

  return (
    <div className="app-layout">
      {showSetup && !user?.company_id && !isOwnerOrStaff && <CompanySetupModal onComplete={() => setShowSetup(false)} />}
      <aside className={`sidebar ${mobileNav ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img src={companyLogo || PANZIX_LOGO_URL} alt={companyName || 'Panzix Global'} className="sidebar-logo" />
          <div className="sidebar-brand">
            <h1>{companyName || 'Panzix HR AI Workforce'}</h1>
            <p>{companyName ? 'Powered by Panzix' : 'Panzix Global'}</p>
          </div>
        </div>
        <nav className="sidebar-nav">
          {renderNavItems()}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user" onClick={() => { navigate('/settings'); setMobileNav(false); }} style={{ cursor: 'pointer' }} data-testid="profile-to-settings">
            {profileImage ? (
              <img src={profileImage} alt={user?.name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div className="user-avatar-sm">{user?.name?.[0]?.toUpperCase() || 'U'}</div>
            )}
            <div className="user-info-sm">
              <p style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{user?.name || 'User'} {getRoleBadge()}</p>
              <p>{user?.email || ''}</p>
            </div>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }} className="nav-item" style={{ marginTop: 8, width: '100%' }} data-testid="logout-btn">
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
          <div style={{ display: 'flex', gap: 12, padding: '10px 16px 0', justifyContent: 'center' }}>
            <a href="https://customer-assets.emergentagent.com/job_ai-employees-6/artifacts/hz8a6ej1_privacy-policy-panzix-hr-ai-workforce.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, textDecoration: 'none' }}>Privacy</a>
            <a href="https://customer-assets.emergentagent.com/job_ai-employees-6/artifacts/c94tjqmn_terms-of-service-panzix-hr-ai-workforce.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, textDecoration: 'none' }}>Terms</a>
            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>v1.0.0</span>
          </div>
        </div>
      </aside>
      <main className="main-content">
        <header className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setMobileNav(!mobileNav)} className="mobile-menu-btn" data-testid="mobile-menu">
              {mobileNav ? <X size={24} /> : <Menu size={24} />}
            </button>
            <h2>{getPageTitle()}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <NotificationBell />
            <a href="/landing" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', cursor: 'pointer' }} data-testid="powered-by-link">
              <img src={PANZIX_LOGO_URL} alt="Panzix" style={{ width: 22, height: 22, borderRadius: 4 }} />
              <span style={{ fontSize: 13, color: '#64748b' }}>Powered by Panzix AI</span>
            </a>
          </div>
        </header>
        <div className="page-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
